export function profileStorageKey(
  prefix: string,
  profileId?: string,
  legacyUsername?: string,
): string | null {
  const identity = profileId?.trim() || legacyUsername?.trim();
  return identity ? `${prefix}${identity}` : null;
}

/**
 * Moves an existing username-scoped cache to the stable profile ID on the
 * first boot of V2. The old key remains readable by the previous app build
 * until the coordinated rollout is complete.
 */
export function loadProfileStorage(
  targetKey: string | null,
  legacyKey: string | null,
): string | null {
  if (!targetKey) return null;
  try {
    const current = localStorage.getItem(targetKey);
    if (current != null) return current;
    if (!legacyKey || legacyKey === targetKey) return null;
    const legacy = localStorage.getItem(legacyKey);
    if (legacy != null) localStorage.setItem(targetKey, legacy);
    return legacy;
  } catch {
    return null;
  }
}

function profileStorageKeys(prefix: string): string[] {
  const keys: string[] = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
  } catch {
    return [];
  }
  return keys;
}

function migrationMarker(prefix: string): string {
  return `ott_profile_storage_migrated_v2:${prefix}`;
}

/**
 * One-time beta migration requested for the coordinated V2 rollout. Every
 * legacy username-scoped record is merged into the first stable profile ID;
 * collisions keep the most recently updated value. Old keys remain intact so
 * a beta rollback can still read them.
 */
export function migrateAllProfileRecords<T extends { updatedAt?: number }>(
  targetKey: string | null,
  prefix: string,
  recordKey: (key: string, item: T) => string = (key) => key,
): string | null {
  if (!targetKey) return null;
  const marker = migrationMarker(prefix);
  try {
    if (localStorage.getItem(marker) === '1') return localStorage.getItem(targetKey);
    const merged: Record<string, T> = {};
    profileStorageKeys(prefix).forEach((key) => {
      try {
        const value = JSON.parse(localStorage.getItem(key) || '{}') as Record<string, T>;
        if (!value || Array.isArray(value) || typeof value !== 'object') return;
        Object.entries(value).forEach(([itemKey, item]) => {
          itemKey = recordKey(itemKey, item);
          const currentTime = Number(merged[itemKey]?.updatedAt || 0);
          const candidateTime = Number(item?.updatedAt || 0);
          if (!merged[itemKey] || candidateTime >= currentTime) merged[itemKey] = item;
        });
      } catch { /* ignore invalid legacy data */ }
    });
    const raw = JSON.stringify(merged);
    localStorage.setItem(targetKey, raw);
    localStorage.setItem(marker, '1');
    return raw;
  } catch {
    return loadProfileStorage(targetKey, null);
  }
}

export function migrateAllProfileLists<T>(
  targetKey: string,
  prefix: string,
  itemKey: (item: T) => string,
  updatedAt: (item: T) => number,
): string | null {
  const marker = migrationMarker(prefix);
  try {
    if (localStorage.getItem(marker) === '1') return localStorage.getItem(targetKey);
    const merged = new Map<string, T>();
    profileStorageKeys(prefix).forEach((key) => {
      try {
        const value = JSON.parse(localStorage.getItem(key) || '[]') as T[];
        if (!Array.isArray(value)) return;
        value.forEach((item) => {
          const keyForItem = itemKey(item);
          if (!keyForItem) return;
          const current = merged.get(keyForItem);
          if (!current || updatedAt(item) >= updatedAt(current)) merged.set(keyForItem, item);
        });
      } catch { /* ignore invalid legacy data */ }
    });
    const raw = JSON.stringify(Array.from(merged.values()));
    localStorage.setItem(targetKey, raw);
    localStorage.setItem(marker, '1');
    return raw;
  } catch {
    return loadProfileStorage(targetKey, null);
  }
}
